/**
 * Types for the new TG4 Player API (@tg4/api).
 *
 * Hand-ported from `packages/shared/types/cleaned.ts` in the TG4Mono repo, with
 * the zod schemas flattened to plain TypeScript so this app needs no runtime
 * dependency on zod. Dependent enums are inlined from `config.ts`, `rails.ts`,
 * `render.ts`, `provider.ts`, `search.ts` and `peach.ts`.
 *
 * Keep in sync manually when the API's shared types change.
 */

/* -------------------------------------------------------------------------- */
/* Shared enums                                                               */
/* -------------------------------------------------------------------------- */

export type RenderType = 'EPISODIC' | 'BOXSET' | 'STAND_ALONE' | 'LONG_RUNNING'

export type ProviderType = 'BC' | 'YT'

export enum TG4Region {
  WorldWide = 'WW',
  Ireland = 'IOI'
}

export type VideoLanguage = 'ENGLISH' | 'IRISH'

/** Ordered from least to most restrictive. */
export type ContentRating = 'G' | 'PG' | '12A' | '15A' | '16' | '18'

/** Normalised ISO 639 code, e.g. "ga" (the API strips regions like "ga-IE"). */
export type SubtitleLanguage = string

export type SeriesBadge =
  | 'NEW'
  | 'STAFF_PICK'
  | 'TRENDING'
  | 'FEATURED'
  | 'AWARD_WINNING'
  | 'NOMINATED'

/**
 * Raw values of `SeriesResponse.rawCategory`, mirroring `CategoryType` on
 * tvOS and Android. The API types this as a free string, so treat anything
 * outside this list as uncategorised - see `isSeriesCategory`.
 */
export type SeriesCategory =
  | 'Ceol'
  | 'Cula4'
  | 'Cursai Reatha'
  | 'Drama'
  | 'Faisneis'
  | 'Saolchlar'
  | 'Siamsaiocht'
  | 'Sport'
  | 'Boxset'

export type VideoEconomics = 'FREE' | 'AD_SUPPORTED'

export type VideoProviderAccount =
  | 'YOUTUBE_TG4'
  | 'YOUTUBE_BLOC'
  | 'YOUTUBE_CULA4'
  | 'YOUTUBE_SPORT'
  | 'YOUTUBE_TRAD'
  | 'BRIGHTCOVE_MAIN'
  | 'BRIGHTCOVE_FOGHLAIM'
  | 'BRIGHTCOVE_MOLSCEAL'

export type SearchItemType = 'SERIES' | 'VIDEO' | 'CATEGORY' | 'GROUP'


export enum RailResponseType {
  Horizontal = 'HORIZONTAL',
  Carousel = 'CAROUSEL',
  ForYou = 'FOR_YOU',
  ContinueWatching = 'CONTINUE_WATCHING',
  CategoryRail = 'CATEGORY_RAIL',
  Live = 'LIVE',
  SingleContent = 'SINGLE_CONTENT',
  DoubleContent = 'DOUBLE_CONTENT',
  Marketing = 'MARKETING',
  LuckyDip = 'LUCKY_DIP',
  StaffReview = 'STAFF_REVIEW'
}

export type RailGlyphType =
  | 'T'
  | 'G'
  | 'LEFT_X'
  | 'RIGHT_X'
  | 'G_ROTATED_RIGHT'
  | 'C_ROTATED_LEFT'

export type RailGlyphPosition =
  | 'TOP_LEFT'
  | 'TOP_RIGHT'
  | 'TOP_MIDDLE'
  | 'MIDDLE_LEFT'
  | 'MIDDLE_RIGHT'
  | 'BOTTOM_LEFT'
  | 'BOTTOM_RIGHT'
  | 'BOTTOM_MIDDLE'

export type RailAspect = '32:9' | '64:9'

export type RailActionButtonPosition = 'LEFT' | 'RIGHT'

export type ItemType = 'SERIES' | 'VIDEO' | 'LIVE_STREAM' | 'CATEGORY'

/* -------------------------------------------------------------------------- */
/* EPG / live streams                                                         */
/* -------------------------------------------------------------------------- */

export enum TG4Stream {
  TG4 = 'TG4',
  TG4PLUSONE = 'TG4PLUSONE',
  TG4PLUSTWO = 'TG4PLUSTWO',
  CULA4 = 'CULA4',
  ONDEMAND = 'ONDEMAND',
}

export enum TG4StreamState {
  ONAIR = 'ONAIR',
  OFFAIR = 'OFFAIR',
  UPCOMING = 'UPCOMING',
  DISABLED = 'DISABLED',
}

export type EPGItemAvailability = 'IRELAND_ONLY' | 'WORLDWIDE' | 'UNAVAILABLE'

export type LiveStreamLogo =
  | 'TG4'
  | 'TG4_PLUSONE'
  | 'TG4_PLUSTWO'
  | 'CULA4'
  | 'NONE'
  | 'SPORT'
  | 'OIREACHTAS'

export interface CurrentEPGShow {
  /** ISO 8601 datetime */
  startTime: string
  /** ISO 8601 datetime */
  endTime: string | null
  availability: EPGItemAvailability
  title: string
  seriesTitle: string
  descriptionEn?: string | null
  descriptionGa?: string | null
  pcode?: string | null
  scode?: string | null
  referenceId: string | null
  /** ISO 8601 datetime */
  streamStartTime?: string | null
}

export interface CurrentLiveStream {
  stream: TG4Stream
  streamId: string | null
  liveToken: string | null
  region: TG4Region
  offsetSeconds: number
  state: TG4StreamState
  logoType: LiveStreamLogo | null
  logoUrl: string | null
  currentShow: CurrentEPGShow | null
  nextShow: CurrentEPGShow | null
}

export interface CurrentLiveStreamsResponse {
  pollIntervalSeconds: number
  /** ISO 8601 datetime */
  nextChange: string | null
  cacheValidForSeconds: number
  streams: CurrentLiveStream[]
}

/* -------------------------------------------------------------------------- */
/* Images                                                                     */
/* -------------------------------------------------------------------------- */

export interface ImageResponse {
  aspectRatio: number
  original: string
  xLarge: string
  large: string
  thumbnail: string
  altTextEn: string | null
  altTextGa: string | null
}

export interface SeriesImages {
  mainImage: ImageResponse | null
  boxsetImage: ImageResponse | null
  heroImage: ImageResponse | null
}

/* -------------------------------------------------------------------------- */
/* Core content                                                               */
/* -------------------------------------------------------------------------- */

export interface SeriesResponse {
  name: string
  descEn: string
  descGa: string
  taglineEn: string | null
  taglineGa: string | null
  slug: string
  sCode: string | null
  poster: string | null
  renderType: RenderType
  hasDownloadableContent: boolean
  boxsetHeroImage: ImageResponse | null
  boxsetImage: ImageResponse | null
  mainImage: ImageResponse | null
  heroImage: ImageResponse | null
  badge: SeriesBadge | null
  contentRating: ContentRating | null
  subtitles: SubtitleLanguage[]
  rawCategory: string | null
}

export interface VideoResponse {
  vid: string
  referenceId: string | null
  displayTitle: string | null
  descriptionEn: string | null
  descriptionGa: string | null
  taglineEn: string | null
  taglineGa: string | null
  seasonNumber: number | null
  episodeNumber: number | null
  duration: number | null
  provider: ProviderType
  providerId: string
  renderType: RenderType
  customFields: Record<string, any> | null
  metadata: Record<string, any> | null
  offlineEnabled: boolean
  poster: string | null
  /** ISO 8601 datetime */
  airDate: string | null
  pCode: string | null
  tags: string[]
  account: VideoProviderAccount
  accountId: string
  economics: VideoEconomics | null
  geoRestricted: boolean
  image: ImageResponse | null
  contentRating: ContentRating | null
  subtitles: SubtitleLanguage[]
  language: VideoLanguage | null
  seriesTitle: string | null
  /** ISO 8601 datetime */
  scheduleEndAt: string | null
}

export interface SeasonResponse {
  sCode: string | null
  number: number
  image: ImageResponse | null
  subtitles: SubtitleLanguage[]
}

export interface GroupItem {
  type: 'SERIES' | 'VIDEO'
  series?: SeriesResponse
  video?: VideoResponse
}

export interface GroupResponse {
  name: string
  displayEn: string
  displayGa: string
  items: GroupItem[]
}

export interface CategoryResponse {
  displayEn: string
  displayGa: string
  slug: string
  color: string | null
  urlPathEn: string
  urlPathGa: string
  image: ImageResponse | null
}

export interface CategoryResponseWithSubCategories extends CategoryResponse {
  subCategories: CategoryResponseWithSubCategories[]
}

/* -------------------------------------------------------------------------- */
/* Progress-aware content (authenticated requests only)                       */
/* -------------------------------------------------------------------------- */

export interface PeachVideoProgress {
  playbackProgress: number | null
  playbackLastPosition: number | null
  playbackComplete: boolean
}

/** Progress fields are only present when the request is authenticated and progress was loaded. */
export interface VideoResponseWithProgress extends VideoResponse {
  playbackProgress?: number | null
  playbackLastPosition?: number | null
  playbackComplete?: boolean
}

export interface PopulatedSeasonResponse extends SeasonResponse {
  videos: VideoResponseWithProgress[]
}

export interface RecommendationResponse {
  id: string
  videos: VideoResponseWithProgress[] | null
}

/* -------------------------------------------------------------------------- */
/* Rails                                                                      */
/* -------------------------------------------------------------------------- */

export interface SeriesItem {
  type: 'SERIES'
  series: SeriesResponse
}

export interface VideoItem {
  type: 'VIDEO'
  video: VideoResponse
}

export interface LiveStreamItem {
  type: 'LIVE_STREAM'
  stream: CurrentLiveStream
}

export interface CategoryItem {
  type: 'CATEGORY'
  category: CategoryResponse
}

export type RailItemResponse = SeriesItem | VideoItem | LiveStreamItem | CategoryItem

export type SeriesOrVideoResponse = SeriesItem | VideoItem

export type SeriesVideoOrLiveStreamResponse = SeriesItem | VideoItem | LiveStreamItem

/**
 * What `CMSItemCell` renders: a series or a video, with the video's progress
 * fields allowed through.
 *
 * `VideoItem` above carries a bare `VideoResponse` because rails are fetched
 * unauthenticated. Progress fields are all optional, so a `VideoItem` is still
 * assignable here — this widens the union for progress-aware callers (the
 * season list, and later history and bookmarks) without touching the rail types.
 */
export interface VideoItemWithProgress {
  type: 'VIDEO'
  video: VideoResponseWithProgress
}

export type CMSItemResponse = SeriesItem | VideoItemWithProgress

export type GridItemResponse = CMSItemResponse | CategoryItem

//Supported rail items
export const isSupportedRailItem = (item: RailItemResponse): item is SeriesVideoOrLiveStreamResponse =>
  item.type === 'SERIES' || item.type === 'VIDEO' || item.type === 'LIVE_STREAM';

const SERIES_CATEGORIES: SeriesCategory[] = [
  'Ceol',
  'Cula4',
  'Cursai Reatha',
  'Drama',
  'Faisneis',
  'Saolchlar',
  'Siamsaiocht',
  'Sport',
  'Boxset',
];

/**
 * Only these raw values have a `category.*` translation, and a key this app
 * cannot resolve renders its own key path, so an unrecognised category has to
 * be treated as no category at all.
 */
export const isSeriesCategory = (raw: string | null): raw is SeriesCategory =>
  raw !== null && SERIES_CATEGORIES.includes(raw as SeriesCategory);

/** Carried by a `LUCKY_DIP` rail in place of `items`. */
export interface RailLuckyDipItem {
  titleEn: string
  titleGa: string
  subtitleEn: string | null
  subtitleGa: string | null
  imageUrl: string
}

/** The review a `STAFF_REVIEW` rail is built around. */
export interface RailStaffReview {
  titleEn: string
  titleGa: string
  reviewEn: string
  reviewGa: string
  rating: number | null
  authorImageUrl: string | null
  showStaffPickIcon: boolean
}

export interface RailResponse {
  id: string
  type: RailResponseType
  titleEn: string | null
  titleGa: string | null
  subtitleEn: string | null
  subtitleGa: string | null
  backgroundColor: string | null
  textColor: string | null
  backgroundGlyph: RailGlyphType | null
  backgroundGlyphPosition: RailGlyphPosition | null
  backgroundGlyphColor: string | null
  showItemsAsBoxset: boolean
  overlayTextOnImage: boolean
  items: RailItemResponse[]
  image: ImageResponse | null
  actionButtonTextEn: string | null
  actionButtonTextGa: string | null
  actionButtonUrl: string | null
  actionButtonPosition: RailActionButtonPosition | null
  categoryToOpen: CategoryResponse | null
  railAspect: RailAspect | null
  /** Optional upstream, so absent on every rail that is not a lucky dip. */
  luckyDipItems?: RailLuckyDipItem[]
  /** Optional upstream, so absent on every rail that is not a staff review. */
  staffReview?: RailStaffReview
  showCategoryOnSeries?: boolean
}

export interface RailPageResponse {
  rails: RailResponse[]
  /** ISO 8601 datetime. Date of next change - can be used for smart polling with jitter. */
  validUntil: string | null
  /** Poll interval for basic polling. */
  pollIntervalSeconds: number
  /** Random shift to add to validUntil to spread server load for smart polling. */
  maximumPollJitterSeconds: number
}

export interface CategoryDetailResponse {
  category: CategoryResponseWithSubCategories
  parentCategories: CategoryResponse[]
  rails: RailResponse[]
  /** ISO 8601 datetime */
  validUntil: string | null
}

/* -------------------------------------------------------------------------- */
/* Search                                                                     */
/* -------------------------------------------------------------------------- */

export type SearchItemResponse =
  | { type: 'SERIES'; series: SeriesResponse }
  | { type: 'VIDEO'; video: VideoResponse }
  | { type: 'CATEGORY'; category: CategoryResponse }

export interface SearchResponse {
  results: SearchItemResponse[]
}

/* -------------------------------------------------------------------------- */
/* Compatibility shapes (legacy Player API fields, kept during app roll-out)  */
/* -------------------------------------------------------------------------- */

export interface CompatibilityVideoResponse extends VideoResponse {
  id: number
  name: string
  descriptionEng: string | null
  seriesId: number | null
}

export interface CompatibilitySeriesResponse extends SeriesResponse {
  id: number
}

export interface CompatibilitySeasonResponse extends SeasonResponse {
  id: number
  seriesId: number
  playable: boolean
  geoRestricted: boolean
  contentRating: ContentRating | null
}

/** Stripped-down search result shape returned by the Player API. */
export interface CompatibilitySearchItemResponse {
  id: string
  type: SearchItemType
  name: string
  descEn: string
  descGa: string
  thumbnail: string
  seasonNumber?: number
  episodeNumber?: number
  pCode?: string
  sCode?: string
  provider: ProviderType
  airDate?: string
  renderType: RenderType
  image: ImageResponse | null
}

/* -------------------------------------------------------------------------- */
/* Endpoint response envelopes (from packages/shared/types/responses.ts)      */
/* -------------------------------------------------------------------------- */

export type TG4Site = 'TG4' | 'FOGHLAIM' | 'MOLSCEAL' | 'CULA4' | 'BLOC'

/** GET /api/series/:slug */
export interface SingleSeriesResponse {
  series: SeriesResponse
  seasons: SeasonResponse[]
  categories: CategoryResponse[] | null
}

/** GET /api/videos/:vid and GET /api/videos/slug/:slug/:season/:episode */
export interface SingleVideoResponse {
  video: VideoResponseWithProgress
  series?: SeriesResponse | null
  groups?: GroupResponse[]
  season?: SeasonResponse | null
  categories: CategoryResponse[] | null
  nextVideo?: VideoResponse | null
}

/**
 * Envelope around a paged endpoint's results.
 *
 * The two paging styles the API uses both come back in this shape: an offset
 * endpoint echoes `limit`/`skip`, while a cursor endpoint hands back the
 * `nextPageLastItem*` pair to pass into the following request. Everything bar
 * `data` and `hasMore` is optional, since which fields are filled depends on
 * which style the endpoint uses.
 */
export interface CMSPaginatedResponse<T> {
  data: T
  limit?: number
  skip?: number
  total?: number
  hasMore: boolean
  nextPageLastItemId?: string | null
  nextPageLastItemDate?: number | null
}
